/**
 * DonationPrompt - the ask, shown after a chore is marked complete
 * ----------------------------------------------------------------
 * That moment is chosen deliberately: the person has just had the thing the
 * site exists for actually happen. It is the one point where "this costs money
 * to run" lands as context rather than as an interruption.
 *
 * THREE THINGS THIS MUST NOT DO
 *
 * 1. Appear for a student. It never renders unless the server says
 *    `enabled`, and the server answers false for any role except a chore
 *    poster. Students are 16 and 17 year olds and are never asked for money.
 *
 * 2. Nag. Marking chores complete is routine, and an appeal on every single
 *    one would be worse than useless - people stop reading it, and it starts
 *    to feel like a toll on finishing a job. So it stays quiet for a month
 *    after it is dismissed, and for a year after somebody actually gives.
 *
 * 3. Promise anything. Donating buys nothing on this site - no badge, no
 *    priority, nothing anyone else can see. The wording says so plainly,
 *    because posters are the people who choose which student gets paid work
 *    and money must not look like it buys influence over that.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ExternalLink } from 'lucide-react';

import { donationApi } from '../services/api';
import Modal from './Modal';
import { useI18n } from '../hooks/useI18n';

// When the prompt may next appear. Stored per browser, which is enough - the
// worst case of losing it is one extra appeal, and it keeps donation habits
// off the server, where they would sit against the donor's account for no good
// reason.
const SNOOZE_KEY = 'choreconnect_donation_snoozed_until';
const MONTH = 30 * 24 * 60 * 60 * 1000;
const YEAR = 365 * 24 * 60 * 60 * 1000;

function snoozedUntil() {
  const raw = Number(window.localStorage.getItem(SNOOZE_KEY));
  return Number.isFinite(raw) ? raw : 0;
}

function snoozeFor(ms) {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms));
  } catch {
    /* private browsing - one extra appeal is not worth crashing over */
  }
}

/** True if enough time has passed since the last dismissal or donation. */
export function donationPromptIsDue() {
  return Date.now() >= snoozedUntil();
}

const euros = (cents) => `€${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export default function DonationPrompt({ open, onClose }) {
  const { t } = useI18n();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    donationApi
      .config()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch(() => {
        // A failure here must never block finishing a chore. Silence is right.
        if (!cancelled) setConfig({ enabled: false });
      });
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [open]);

  const dismiss = () => {
    snoozeFor(MONTH);
    onClose();
  };

  const give = async (amountCents) => {
    setBusy(true);
    setError('');
    try {
      const { url } = await donationApi.checkout(amountCents);
      // Somebody who reaches Stripe should not be asked again for a long
      // while, whether or not they complete the payment there.
      snoozeFor(YEAR);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Nothing at all unless the server says this account may be asked.
  if (!open || !config?.enabled) return null;

  return (
    <Modal
      open={open}
      onClose={dismiss}
      title={t('donate.title')}
      actions={
        <button type="button" className="btn btn-ghost btn-inline" onClick={dismiss}>
          {t('donate.notJustNow')}
        </button>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-sm)' }}>
        <p>
          {t('donate.niceOne')}
        </p>
        <p>
          {t('donate.runsOnDonations')}
        </p>

        {config.paymentsReady === false && (
          <div className="alert alert-warning">
            <div>{t('support.devNoStripe')}</div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {(config.suggestedCents || []).map((cents) => (
            <button
              key={cents}
              type="button"
              className="btn btn-primary btn-inline"
              disabled={busy}
              onClick={() => give(cents)}
            >
              <Heart size={16} aria-hidden="true" /> {euros(cents)}
            </button>
          ))}
          {/* The box for any other amount is on the support page, not here -
              this pop-up interrupts somebody finishing a job and stays short.
              Following the link counts as dismissing it for a month. */}
          <Link to="/support" className="btn btn-ghost btn-inline" onClick={() => snoozeFor(MONTH)}>
            {t('donate.otherAmountLink')}
          </Link>
        </div>

        <p className="text-sm text-muted">
          {t('donate.oneOff')}
          {config.paymentsReady !== false && (
            <>
              {' '}
              <ExternalLink size={12} aria-hidden="true" /> {t('donate.takenToStripe')}
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}
